import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { ConfluenceIcon } from '@atlaskit/logo';
import { Box } from '@atlaskit/primitives/compiled';
import { ConversationStartersBanner } from '@atlassian/search-ai/conversation-starters-banner';
import { getSAINLayerFrontendExperiment } from '@atlassian/search-ai/get-sain-layer-frontend-experiment';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as ConfluenceSearchResultType } from '@atlassian/search-client/common/types';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { ApplicationModes } from '../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../common/constants/products';
import type { SearchResultProps } from '../../../../common/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { DescriptionBody } from '../../../../common/ui/description-body';
import { LastModified } from '../../../../common/ui/last-modified';
import { getConfluenceEntityIcon } from '../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../common/ui/search-result';
import { getSearchResultType } from '../../../../common/ui/search-result-type';
import { areLLMPoweredFeaturesEnabled } from '../../../../common/utils/entitlement-checks';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../common/utils/search-result-utils';
import { useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { useFireContextMenuButtonClicked } from '../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useFeatures } from '../../../../controllers/store/bootstrap';

import type { confluenceSearchResponse_AtlassianSearchPage$key } from './__generated__/confluenceSearchResponse_AtlassianSearchPage.graphql';

export type ConfluenceSearchResponseProps =
	SearchResultProps<confluenceSearchResponse_AtlassianSearchPage$key>;

export const ConfluenceSearchResponse = ({
	node,
	position,
	cloudId,
	searchQuery: query,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: ConfluenceSearchResponseProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment confluenceSearchResponse_AtlassianSearchPage on ConfluenceSearchResponse {
				__typename
				title
				excerpt
				url
				lastModified
				links {
					base
				}
				content {
					ari
					type
					space {
						ari
						name
						links {
							webui
						}
					}
				}
			}
		`,
		node,
	);

	const [features] = useFeatures();
	const isSideBySideEnabled = features?.sideBySide?.enabled;
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const { getFeatureGate, isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();

	const model = useMemo(
		() => ({
			id: data?.content?.ari || '',
			title: data?.title,
			description: data?.excerpt,
			type: data?.content?.type || '',
			url: `${data?.links?.base}${data?.url}`,
			lastModified: data?.lastModified,
			isVerified: null,
			space:
				data?.content?.space?.ari && data?.content?.space?.name
					? {
							id: data.content.space.ari,
							name: data.content.space.name,
							webUiLink: data.content.space.links?.webui,
						}
					: null,
			socialSignal: null,
		}),
		[data],
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(model.id);
		let containerId: string | null = null;
		if (model.space?.id) {
			const [, , , spaceId] = getProductAndIDFromARI(model.space.id);
			containerId = spaceId;
		}

		return {
			id: model.id,
			containerId,
			spaceId: containerId,
			contentType: model.type,
			entityType: model.type,
			contentId,
			product,
			isSideBySideEnabled,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [model, isSideBySideEnabled]);

	const searchResultForConversationStarters = useMemo(() => {
		const { serveExperiment: serveConversationStartersExperiment } = getSAINLayerFrontendExperiment(
			{
				abTestId: 'kd_convo_starters_in_search',
			},
		);

		const isConversationStartersEnabled =
			serveConversationStartersExperiment || getFeatureGate('kd_convo_starters_dogfooding');

		const isPageOrBlogpost = ['page', 'blogpost'].includes(model.type);

		const searchResultForConversationStarters =
			isPageOrBlogpost &&
			isConversationStartersEnabled &&
			position === 0 &&
			appMode === ApplicationModes.Rovo &&
			areLLMPoweredFeaturesEnabled(appMode, isAdminHubAIEnabled, isRovoLLMEnabled)
				? { ari: model.id, url: model.url }
				: null;

		return searchResultForConversationStarters;
	}, [
		appMode,
		getFeatureGate,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		model.id,
		model.type,
		model.url,
		position,
	]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const metadataParts: [string, ReactNode][] = [
		['type', getSearchResultType(model.type as unknown as ConfluenceSearchResultType)],
		['last-modified', model.lastModified && <LastModified lastModified={model.lastModified} />],
	];

	let icon: React.JSX.Element;
	if (model.type) {
		icon = getConfluenceEntityIcon({
			iconUrl: undefined,
			type: model.type,
			size: 'medium',
		});
	} else {
		icon = <ConfluenceIcon size="medium" shouldUseNewLogoDesign />;
	}

	return (
		<SearchResult
			id={model.id}
			product={ProductKeys.Confluence}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={getConfluenceUrlWithAnalyticsQueryParams({
				url: model.url,
				query,
				searchSessionId,
			})}
			title={enrichExcerpt(model.title)}
			icon={icon}
			headerMetadataParts={metadataParts}
			description={<DescriptionBody description={processExcerpt(model.description)} />}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			hideSummarizeInChat={!!searchResultForConversationStarters}
			footer={
				<Box>
					{cloudId && searchResultForConversationStarters && (
						<ConversationStartersBanner
							primaryProduct={primaryProduct}
							cloudId={cloudId}
							searchResult={searchResultForConversationStarters}
							analyticsAttributes={{ resultMetadata: metadata, ...commonAttributes }}
						/>
					)}
				</Box>
			}
		/>
	);
};
